import {ByteRange, HttpStorage, IcechunkStore, RequestOptions, Storage} from 'icechunk-js';
import * as zarr from 'zarrita';

import {WeatherColumns} from 'src/lib/api/series';

import {getIcechunkStoreUrl} from './catalog';
import {DynamicalDatasetDescriptor} from './datasets';

/**
 * dynamical.org appends new init times by committing new snapshots, so the branch
 * pointer must be re-read periodically for a long-lived tab to see fresh forecasts.
 */
const DATASET_TTL_MS = 15 * 60 * 1000;
const MAX_CONCURRENT_REQUESTS = 8;
const MAX_RETRIES = 2;
/**
 * Per-attempt cap on any single S3 read. A connection that stalls without
 * erroring would otherwise leave the promise pending forever — and the API
 * fallback only fires on rejection, so a hung read means a permanently
 * spinning chart.
 */
const REQUEST_TIMEOUT_MS = 15 * 1000;

/** options with the timeout signal merged in (caller's signal still honored). */
function withRequestTimeout(options: RequestOptions | undefined): RequestOptions | undefined {
  if (typeof AbortSignal === 'undefined' || typeof AbortSignal.timeout !== 'function') {
    return options;
  }
  const timeoutSignal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);
  const signal =
    options?.signal && typeof AbortSignal.any === 'function'
      ? AbortSignal.any([options.signal, timeoutSignal])
      : (options?.signal ?? timeoutSignal);
  return {...options, signal};
}

/** Serializes access to the S3 bucket so a large chunk fan-out can't stampede. */
class Semaphore {
  private queue: Array<() => void> = [];
  private active = 0;

  constructor(private readonly limit: number) {}

  async run<T>(task: () => Promise<T>): Promise<T> {
    if (this.active >= this.limit) {
      await new Promise<void>((resolve) => this.queue.push(resolve));
    }
    this.active += 1;
    try {
      return await task();
    } finally {
      this.active -= 1;
      this.queue.shift()?.();
    }
  }
}

/**
 * Storage wrapper adding a concurrency cap and retry-with-backoff over every
 * object read (metadata, manifests, and chunks all flow through getObject).
 */
class ThrottledStorage implements Storage {
  constructor(
    private readonly inner: Storage,
    private readonly semaphore: Semaphore
  ) {}

  async getObject(path: string, range?: ByteRange, options?: RequestOptions): Promise<Uint8Array> {
    return this.semaphore.run(async () => {
      let lastError: unknown;
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
          // Each attempt gets its own timeout; a timed-out (aborted) attempt is
          // transient and retried, unlike a caller abort (checked below).
          return await this.inner.getObject(path, range, withRequestTimeout(options));
        } catch (error) {
          // NotFoundError is a real answer (missing chunk/ref), not a transient
          // failure; icechunk-js relies on it propagating.
          if ((error as Error).name === 'NotFoundError' || options?.signal?.aborted) {
            throw error;
          }
          lastError = error;
          if (attempt < MAX_RETRIES) {
            await new Promise((resolve) => setTimeout(resolve, 250 * 2 ** attempt));
          }
        }
      }
      throw lastError;
    });
  }

  exists(path: string, options?: RequestOptions): Promise<boolean> {
    // Route through the semaphore like getObject: IcechunkStore.open() probes
    // refs/manifests via exists(), so an unthrottled path lets a parallel open
    // fan out existence checks past the S3 concurrency budget.
    return this.semaphore.run(() => this.inner.exists(path, withRequestTimeout(options)));
  }

  listPrefix(prefix: string): AsyncIterable<string> {
    return this.inner.listPrefix(prefix);
  }
}

export type DatasetCoords = {
  /** Init times as epoch milliseconds, ascending. */
  initTimesMs: number[];
  /** Lead times in hours, ascending. */
  leadTimeHours: number[];
  /** 1-D spatial coordinates: degrees for geographic grids, meters for projected. */
  yCoords: Float64Array;
  xCoords: Float64Array;
  /** CRS attributes from the spatial_ref variable (projected grids only). */
  spatialRefAttrs: Record<string, unknown> | null;
};

export type DynamicalDataset = {
  descriptor: DynamicalDatasetDescriptor;
  storeUrl: string;
  coords: DatasetCoords;
  arrays: {[column in WeatherColumns]?: zarr.Array<zarr.DataType, IcechunkStore>};
  openedAt: number;
};

const semaphore = new Semaphore(MAX_CONCURRENT_REQUESTS);
const datasetCache = new Map<string, Promise<DynamicalDataset>>();

function toNumberArray(data: ArrayLike<number | bigint>): number[] {
  const out = new Array<number>(data.length);
  for (let i = 0; i < data.length; i += 1) {
    out[i] = Number(data[i]);
  }
  return out;
}

async function readCoord(
  root: zarr.Location<IcechunkStore>,
  name: string
): Promise<{data: ArrayLike<number | bigint>; attrs: Record<string, unknown>}> {
  const arr = await zarr.open(root.resolve(name), {kind: 'array'});
  const chunk = await zarr.get(arr as zarr.Array<zarr.NumberDataType | zarr.BigintDataType, IcechunkStore>);
  return {data: chunk.data as ArrayLike<number | bigint>, attrs: arr.attrs as Record<string, unknown>};
}

function coordUnitToMsFactor(units: string): number {
  if (units.startsWith('seconds')) return 1000;
  if (units.startsWith('milliseconds')) return 1;
  if (units.startsWith('hours')) return 3600 * 1000;
  throw new Error(`Unsupported time coordinate units: ${units}`);
}

async function openDatasetUncached(
  descriptor: DynamicalDatasetDescriptor
): Promise<DynamicalDataset> {
  const storeUrl = await getIcechunkStoreUrl(descriptor);
  const storage = new ThrottledStorage(new HttpStorage(storeUrl), semaphore);
  const store = await IcechunkStore.open(storage, {branch: 'main'});
  const root = zarr.root(store);

  const [initTime, leadTime] = await Promise.all([
    readCoord(root, 'init_time'),
    readCoord(root, 'lead_time'),
  ]);
  const initUnits = String(initTime.attrs.units ?? 'seconds since 1970-01-01');
  const initFactor = coordUnitToMsFactor(initUnits);
  const initTimesMs = toNumberArray(initTime.data).map((v) => v * initFactor);
  // lead_time is stored in seconds; the HF API's dim_2 convention is hours.
  const leadUnits = String(leadTime.attrs.units ?? 'seconds');
  const leadToHours = leadUnits.startsWith('seconds') ? 1 / 3600 : 1;
  const leadTimeHours = toNumberArray(leadTime.data).map((v) => v * leadToHours);

  const yName = descriptor.gridKind === 'geographic' ? 'latitude' : 'y';
  const xName = descriptor.gridKind === 'geographic' ? 'longitude' : 'x';
  const [yCoord, xCoord] = await Promise.all([readCoord(root, yName), readCoord(root, xName)]);

  let spatialRefAttrs: Record<string, unknown> | null = null;
  if (descriptor.gridKind === 'projected') {
    const spatialRef = await zarr.open(root.resolve('spatial_ref'), {kind: 'array'});
    spatialRefAttrs = spatialRef.attrs as Record<string, unknown>;
  }

  const arrays: DynamicalDataset['arrays'] = {};
  await Promise.all(
    Object.entries(descriptor.storeVariableByColumn).map(async ([column, storeVariable]) => {
      const arr = await zarr.open(root.resolve(storeVariable), {kind: 'array'});
      // Dimension order is (init_time[, ensemble_member], lead_time, y, x); a shape
      // that doesn't line up with the coordinate arrays means the store layout
      // changed under us, and the caller must fall back to the API.
      const expectedShape = [
        initTimesMs.length,
        ...(descriptor.hasEnsemble ? [arr.shape[1]] : []),
        leadTimeHours.length,
        yCoord.data.length,
        xCoord.data.length,
      ];
      if (
        arr.shape.length !== expectedShape.length ||
        !arr.shape.every((size, i) => size === expectedShape[i])
      ) {
        throw new Error(
          `${descriptor.datasetId}/${storeVariable} shape [${arr.shape}] does not match coordinates`
        );
      }
      arrays[column as WeatherColumns] = arr;
    })
  );

  return {
    descriptor,
    storeUrl,
    coords: {
      initTimesMs,
      leadTimeHours,
      yCoords: Float64Array.from(toNumberArray(yCoord.data)),
      xCoords: Float64Array.from(toNumberArray(xCoord.data)),
      spatialRefAttrs,
    },
    arrays,
    openedAt: Date.now(),
  };
}

export async function openDataset(
  descriptor: DynamicalDatasetDescriptor
): Promise<DynamicalDataset> {
  const cached = datasetCache.get(descriptor.datasetId);
  if (cached) {
    const dataset = await cached.catch(() => null);
    if (dataset && Date.now() - dataset.openedAt < DATASET_TTL_MS) {
      return dataset;
    }
  }
  const opening = openDatasetUncached(descriptor);
  datasetCache.set(descriptor.datasetId, opening);
  try {
    return await opening;
  } catch (error) {
    datasetCache.delete(descriptor.datasetId);
    throw error;
  }
}

export function clearDatasetCacheForTesting() {
  datasetCache.clear();
}
